import { useState, useCallback, useMemo } from 'react';
import {
	Button,
	PlusIcon,
	TeamOutline,
	GetRowsParams,
	InfiniteScrollList,
	isReddit,
	isSpotify,
} from '@aleph/shared-components';
import { RedditHeaderSearch } from 'wb-reddit/app/common/components/headerSearch';
import RedditNotifications from 'wb-reddit/app/common/components/notifications';
import { useQuery, useLazyQuery } from '@apollo/client';
import {
	CreateUserGroupMutationCreateUserGroup,
	RedditAdvertiserListDocument,
	RedditAdvertiserListQuery,
	RedditUserGroupsListDocument,
	RedditUserGroupsListQuery,
	RedditUserGroupsListQueryUsers,
	UpdateUserGroupMutationUpdateUserGroup,
} from 'wb-reddit/graphql/generated';
import UserGroupItem from 'wb-reddit/app/routes/teams/components/group';
import RedditTeamMutatorContainer from 'wb-reddit/app/routes/teams/mutator/container';
import useRedditTeamMutationStore from 'wb-reddit/app/routes/teams/hooks/useRedditTeamMutationStore';
import { newTeamData, parseTeamOwnerFromFromQueryUser, User } from 'wb-reddit/app/routes/teams/utils';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import RedditTeamLoader from 'wb-reddit/app/routes/teams/components/loader';
import { useLayoutRef } from 'wb-reddit/app/routes/context/layoutContext';
import useUpdateRows, { HandleUpdateRow } from 'wb-reddit/app/routes/hooks/useUpdateRows';
import {
	AuthGraphAdvertiserListDocument,
	AuthGraphAdvertiserListQuery,
	AuthGraphUserGroupsListDocument,
	AuthGraphUserGroupsListQuery,
} from 'wb-spotify/graphql/authgraph/generated';
import { useTranslation } from 'react-i18next';
import useSpotifyRolePermissions from 'wb-spotify/app/common/hooks/useSpotifyPermissions';

type BeGroup = UpdateUserGroupMutationUpdateUserGroup | CreateUserGroupMutationCreateUserGroup;

export type TeamUser = Omit<RedditUserGroupsListQueryUsers, 'user'> & {
	user: User
};

export type TeamRow = Omit<BeGroup, 'users'> & {
	users?: TeamUser[]
};

export type HandleUpdateTeamRow = HandleUpdateRow<TeamRow>;

const RedditTeamsContainer = () => {
	const { t } = useTranslation();
	const { mutatingTeam, setMutatingTeam } = useRedditTeamMutationStore();
	const [search, setSearch] = useState<string>('');

	const { data: advertiserData } = useQuery(
		isReddit()
			? RedditAdvertiserListDocument
			: AuthGraphAdvertiserListDocument,
		{
			fetchPolicy: 'network-only', // TODO: remove once https://github.com/apollographql/apollo-client/issues/5419
			variables:   {
				search: `*${''}*`,
			},
			skip: isSpotify(),
		},
	);

	const [getTeams] = useLazyQuery(
		isReddit()
			? RedditUserGroupsListDocument
			: AuthGraphUserGroupsListDocument,
	);

	const getRows = useCallback(async (params: GetRowsParams<string | null>) => {
		const { data } = await getTeams({
			variables: {
				cursor: params.metaData ?? undefined,
				search: search ? `*${search}*` : '**',
			},
			fetchPolicy: 'network-only',
		});

		if (isReddit()) {
			const redditData = data as unknown as RedditUserGroupsListQuery;

			return {
				data: (redditData?.userGroups.items ?? [])
					.map(group => ({
						...group,
						// Remove the __typename from the user object and parse the user
						users: group.users?.map(({ __typename, ...user }) => ({
							...user,
							user: parseTeamOwnerFromFromQueryUser(user.user),
						})),
					})),
				hasNextPage: !!redditData?.userGroups.cursor,
				metaData:    redditData?.userGroups.cursor ?? null,
			};
		}

		const spotifyData = data as unknown as AuthGraphUserGroupsListQuery;

		return {
			data:        spotifyData?.userGroups?.items ?? [],
			hasNextPage: !!spotifyData?.userGroups?.cursor,
			metaData:    spotifyData?.userGroups?.cursor ?? null,
		};
	}, [search]);

	const platformUser = useUserMetaData(import.meta.env.VITE_PACKAGE_APP);

	const parentElement = useLayoutRef();

	const {
		handleUpdateRow,
		exposedMethods,
	} = useUpdateRows<TeamRow>();

	const advertisers = useMemo(
		() => (isReddit()
			? (advertiserData as unknown as RedditAdvertiserListQuery)?.agencyAccounts?.items ?? []
			: (advertiserData as unknown as AuthGraphAdvertiserListQuery)?.advertiserAccounts?.items ?? []),
		[advertiserData],
	);
	const { canCreateTeam: spotifyCanCreateTeam } = useSpotifyRolePermissions();

	const canCreateTeam = useMemo(() => {
		if (isReddit()) {
			return platformUser?.isAdmin;
		}

		return spotifyCanCreateTeam;
	}, [platformUser, spotifyCanCreateTeam]);

	return (
		<>
			{mutatingTeam && <RedditTeamMutatorContainer updateRow={handleUpdateRow} />}
			<div className="flex flex-col gap-[16px] w-full sticky top-0 left-0 bg-gray-0 z-10">
				<div
					className="flex h-[64px] items-center justify-between border-b border-b-black"
					data-testid="teams-header-container"
				>
					<div
						className="flex flex-1 items-center gap-[16px] h-full pr-[16px]
						text-[18px] text-gray-800 font-medium"
					>
						<TeamOutline
							width={24}
							height={24}
						/>
						Teams
					</div>

					<RedditHeaderSearch
						search={search}
						setSearch={setSearch}
					/>

					{isReddit() && (
						<div className="pl-[24px] h-full flex items-center">
							<RedditNotifications />
						</div>
					)}
				</div>

				{
					canCreateTeam && (
						<div>
							<Button
								title={t('sc: new', 'New')}
								leftNode={<PlusIcon />}
								onClick={() => setMutatingTeam(newTeamData)}
							/>
						</div>
					)
				}

				<div
					className="flex whitespace-nowrap font-bold text-black border-b border-b-200
						h-[36px] items-center mb-[-16px] bg-gray-0"
				>
					{/* TODO translate */}
					<div className="px-[8px] flex-1 min-w-[250px]">Teams</div>
					<div className="px-[8px] w-[203px]">Team admins</div>
					<div className="px-[8px] w-[172px]">Members</div>
					<div className="px-[8px] w-[162px]">Advertisers</div>
					<div className="px-[8px] w-[128px]">Accounts</div>
					<div className="px-[8px] w-[100px]">SalesForce</div>
					<div className="w-[70px]" />
				</div>
			</div>

			<InfiniteScrollList<string | null, TeamRow>
				getRows={getRows}
				parentElement={parentElement?.current}
				exposedMethods={exposedMethods}
				renderItem={({ props, data, isLoaderRow }) => {
					if (isLoaderRow) {
						return <RedditTeamLoader key={props.key} />;
					}

					return (<UserGroupItem
						{...props}
						group={data}
						deleteRow={handleUpdateRow}
						advertisers={advertisers}
					/>);
				}}
			/>
		</>
	);
};

export default RedditTeamsContainer;
